import styled from '@emotion/styled';
import { Checkbox } from 'antd';
import { useContext, useEffect, useState, useRef, useLayoutEffect, useCallback } from 'react';
import SafeImage from 'components/SafeImage';
import Bricks from 'bricks.js';
import { LinkOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { useTranslate } from '@tolgee/react';
import { useStoreInfo } from '@knowledge/common/components/ItemSelectV2/useStoreInfo';
import { OperateValueContext, SpuSelectContext } from './context';
import type { SpuSelectMap, ToggleSku, ToggleSpu } from '@knowledge/common/helpers/sku';

type GridViewProps = {
  spus: ItemSelectV2.Item[];
};

const GridView = (props: GridViewProps) => {
  const { spus } = props;
  const { foldState } = useContext(OperateValueContext);
  const bricksRef = useRef<Bricks.Instance>();
  useEffect(() => {
    const instance = Bricks({
      packed: 'data-packed',
      container: document.getElementById('masonry')!,
      sizes: [{ columns: 2, gutter: 16 }],
    });
    bricksRef.current = instance;
    instance.pack();
  }, []);

  const { spuSelectMap, toggleSku, toggleSpu } = useContext(SpuSelectContext);

  const repack = useCallback(() => {
    const items = document.querySelectorAll('#masonry > div');
    document.getElementById('masonry')!.removeAttribute('style');
    items.forEach((item) => {
      item.removeAttribute('data-packed');
      item.removeAttribute('style');
    });
    requestAnimationFrame(() => {
      bricksRef.current?.pack();
    });
  }, []);

  useLayoutEffect(() => {
    repack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foldState, spus]);

  return (
    <Grid id="masonry">
      {spus.map((spu) => {
        return (
          <Item
            selectedInfo={spuSelectMap[spu.internalItemId] || {}}
            toggleSku={toggleSku!}
            toggleSpu={toggleSpu!}
            repack={repack}
            item={spu}
            key={spu.internalItemId}
          />
        );
      })}
    </Grid>
  );
};

type ItemProps = {
  selectable?: boolean;
  item: ItemSelectV2.Item;
  repack?: () => void;
  selectedInfo: SpuSelectMap[string];
  toggleSku: ToggleSku;
  toggleSpu: ToggleSpu;
};

export const Item = (props: ItemProps) => {
  const { selectable = true, item, repack, selectedInfo, toggleSku, toggleSpu } = props;
  const { t } = useTranslate();
  const { foldState } = useContext(OperateValueContext);
  const [open, setOpen] = useState(foldState);
  useEffect(() => {
    setOpen(foldState);
  }, [foldState]);

  const {
    itemName,
    imageUrl: [imageUrl] = [],
    externalItemId: spuId,
    internalItemId,
    skuInfo = [],
    itemUrl,
    itemSku,
  } = item;
  const storeStr = useStoreInfo(item);
  return (
    <div>
      <div className="h-[80px] flex items-center">
        {selectable && (
          <Checkbox
            className="!mr-[8px] flex-shrink-0"
            checked={selectedInfo.checked}
            indeterminate={selectedInfo.indeterminate}
            onChange={(e) => {
              toggleSpu(internalItemId, e.target.checked);
            }}
          />
        )}
        <div className="basis-auto overflow-hidden flex flex-grow">
          <div className="flex-shrink-0 !mr-[8px]">
            <Image hasSmallPic="both" src={imageUrl} width={80} height={80} />
          </div>
          <div className="basis-auto flex-grow overflow-hidden">
            <div className="h-[40px] flex items-start">
              <div
                title={itemName}
                className="w-full group line-clamp-2 break-all cursor-pointer leading-[20px] hover:text-[#3366ff]"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(itemUrl, '_blank');
                }}
              >
                <LinkOutlined className="!hidden float-right bg-[var(--fett-chat-bubble-agent)] !text-[var(--fett-scenario-accent-text)] h-[20px] w-[20px] !leading-[20px] group-hover:!block" />
                {itemName}
              </div>
            </div>
            <div className="h-[36px] mt-[4px] leading-[18px] text-[12px] text-[var(--fett-text-tertiary)]">
              <div className="truncate">
                {t('marketing.店铺')}: {storeStr}
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center truncate basis-auto overflow-hidden mr-[8px]">
                  <span>ID: {spuId}</span>
                  <div className="mx-[4px]">|</div>
                  <span className="truncate">
                    {t('aide.主商品货号')}: {itemSku || '-'}
                  </span>
                </div>
                <div
                  onClick={() => {
                    setOpen(open === 'fold' ? 'unfold' : 'fold');
                    repack?.();
                  }}
                  className="flex-shrink-0 cursor-pointer hover:text-[#3366ff]"
                >
                  {t('knowledge.共{{x}}个SKU', { x: skuInfo?.length })}
                  {open === 'fold' ? (
                    <DownOutlined className="ml-[4px]" />
                  ) : (
                    <UpOutlined className="ml-[4px]" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {open === 'unfold' && skuInfo.length > 0 && (
        <div className="pl-[22px] mt-[8px]">
          <div className="border-0 border-t border-solid border-[var(--fett-border-default)] flex flex-wrap overflow-hidden">
            {skuInfo.map((sku) => {
              const content = (
                <div
                  title={`${sku.sellerSku}(${sku.skuName})`}
                  className="w-full truncate break-all"
                >
                  {sku.sellerSku}({sku.skuName})
                </div>
              );
              return (
                <div
                  key={sku.skuId}
                  className="basis-1/2 flex-shrink-0 flex-grow-0 mt-[9px] pr-[8px] overflow-hidden"
                >
                  {selectable ? (
                    <Checkbox
                      className={css`
                        overflow: hidden;
                        width: 100%;
                        word-break: break-all;
                        span + span {
                          width: 100%;
                          overflow: hidden;
                        }
                      `}
                      checked={selectedInfo?.skus?.[sku.skuId]?.checked}
                      onChange={(e) => {
                        toggleSku(internalItemId, sku.skuId, e.target.checked);
                      }}
                    >
                      {content}
                    </Checkbox>
                  ) : (
                    content
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const Grid = styled.div`
  & > div {
    width: calc(50% - 8px);
    box-sizing: border-box;
    border: 1px solid var(--fett-border-strong);
    border-radius: 2px;
    padding: 10px 13px 12px 8px;
  }
`;

export const Image = styled(SafeImage)`
  width: 80px;
  height: 80px;
`;

export default GridView;
